/**
 * AR-445 — the Phase-1 navigation and every screen it reaches, in one place.
 *
 * The prototype (v17, `NAV` / `NAV9` / `PH_DROP` / `PH_SOLO` / `PH_KIDS` /
 * `PH_ACTS` / `PH_LABEL_MAP`) draws the platform as four groups of Level-1 rows
 * — the management loop, Workspace, Network and WorldVia Me — each row a module
 * with an optional Level-2 column. Phase 1 drops five modules, renames two and
 * strips the column from four. This file is the result of applying those rules
 * for Phase 1, so the rail, the Level-2 column, the mobile tab bar, the router
 * and the Bookmarks panel all read one list and cannot disagree.
 *
 * The later-phase modules are listed with the phase they arrive in rather than
 * left out, so the gating is a rule the tests can check instead of an absence.
 */

/** Every flag the Phase-1 front end needs, parent first — the Account center's pattern. */
export const PHASE1_FLAGS = ['proMigration', 'proMigrationPhase1'] as const;

/** The migration phase this build serves. */
export const PHASE1_PHASE = 1;

/** WorldVia.pro, where the CRM and the Academy still run in Phase 1. */
export const PRO_CRM_URL = 'https://worldvia.pro/pro-crm';
export const PRO_ACADEMY_URL = 'https://worldvia.pro/academy';

// ---------------------------------------------------------------------------
// Pages
// ---------------------------------------------------------------------------

/**
 * Every Phase-1 page that renders inside the Phase-1 layout, keyed by a stable
 * id. The id is what the router, the Level-2 column, the bookmarks store and
 * the page registry (`pages/phase1/index.tsx`) all key on.
 */
export const PHASE1_PAGES = {
  // Marketing — the storefront and the pages its column names (D-PH1-MKT-*)
  marketing: { path: '/marketing', title: 'Marketing', module: 'marketing' },
  'marketing-web-profiles': { path: '/marketing/web-profiles', title: 'Web Profiles', module: 'marketing' },
  'marketing-website': { path: '/marketing/website', title: 'Website', module: 'marketing' },
  'marketing-email': { path: '/marketing/email', title: 'Email Marketing', module: 'marketing' },
  'marketing-direct-mail': { path: '/marketing/direct-mail', title: 'Direct Mail', module: 'marketing' },
  'marketing-social': { path: '/marketing/social', title: 'Social Media', module: 'marketing' },
  'marketing-promo-products': { path: '/marketing/promo-products', title: 'Order Promo Products', module: 'marketing' },
  'marketing-email-newsletters': { path: '/marketing/email-newsletters', title: 'Email Newsletters', module: 'marketing' },
  'marketing-dynamic-promotions': { path: '/marketing/dynamic-promotions', title: 'Dynamic Promotions', module: 'marketing' },
  'marketing-design-studio': { path: '/marketing/design-studio', title: 'Design Studio', module: 'marketing' },
  'marketing-playbooks': { path: '/marketing/playbooks', title: 'Marketing Playbooks', module: 'marketing' },

  // CRM (legacy) — one SSO door, reached from the row and from the old CRM addresses
  crm: { path: '/crm', title: 'CRM', module: 'crm' },
  'crm-pipeline': { path: '/crm/pipeline', title: 'Pipeline', module: 'crm' },
  'crm-clients': { path: '/crm/clients', title: 'Clients', module: 'crm' },
  'crm-trips': { path: '/crm/trips', title: 'Trips + Proposals', module: 'crm' },

  // Booking — the Phase-1 landing, its explainers, and the launch pages (D-PH1-BK-*)
  booking: { path: '/booking', title: 'Booking', module: 'booking' },
  'booking-hq-cruise-space': { path: '/booking/hq-cruise-space', title: 'HQ Cruise Space', module: 'booking' },
  'booking-group-sailings': { path: '/booking/hq-cruise-space/live', title: 'HQ Cruise Space: Live space', module: 'booking' },
  'booking-rate-programs': { path: '/booking/rate-programs', title: 'Rate Programs', module: 'booking' },
  'booking-about-trio': { path: '/booking/about-trio', title: 'About TRIO', module: 'booking' },
  'booking-about-travelport': { path: '/booking/about-travelport-cruise', title: 'About Travelport Cruise', module: 'booking' },
  'booking-about-vax': { path: '/booking/about-vax', title: 'About VAX', module: 'booking' },
  'booking-about-taap': { path: '/booking/about-expedia-taap', title: 'About Expedia TAAP', module: 'booking' },
  'booking-agent-air': { path: '/booking/agent-air', title: 'Agent Air', module: 'booking' },
  'booking-launch-trio': { path: '/booking/launch/trio', title: 'Book in TRIO', module: 'booking' },
  'booking-launch-travelport': { path: '/booking/launch/travelport-cruise', title: 'Book in Travelport Cruise', module: 'booking' },
  'booking-launch-vax': { path: '/booking/launch/vax', title: 'Book in VAX', module: 'booking' },
  'booking-launch-taap': { path: '/booking/launch/expedia-taap', title: 'Book in Expedia TAAP', module: 'booking' },

  // Network
  calendar: { path: '/calendar', title: 'Calendar', module: 'calendar' },
  academy: { path: '/academy', title: 'Academy', module: 'academy' },
  suppliers: { path: '/suppliers', title: 'Suppliers', module: 'suppliers' },
  'suppliers-yours': { path: '/suppliers/yours', title: 'Your Suppliers', module: 'suppliers' },
  'suppliers-promotions': { path: '/suppliers/promotions', title: 'Promotions', module: 'suppliers' },
  'suppliers-fams': { path: '/suppliers/fams-agent-deals', title: 'FAMs + Agent Deals', module: 'suppliers' },
  news: { path: '/news', title: 'News', module: 'news' },
  'everything-worldvia': { path: '/everything-worldvia', title: 'Everything WorldVia', module: 'everything' },
  'everything-tln-agent-universe': {
    path: '/everything-worldvia/tln-agent-universe',
    title: 'TLN Agent Universe',
    module: 'everything',
  },

  // WorldVia Me
  support: { path: '/support', title: 'Support', module: 'support' },
} as const satisfies Record<string, { path: string; title: string; module: string }>;

export type Phase1PageId = keyof typeof PHASE1_PAGES;
export const PHASE1_PAGE_IDS = Object.keys(PHASE1_PAGES) as Phase1PageId[];

export const phase1Path = (id: Phase1PageId): string => PHASE1_PAGES[id].path;

/**
 * Router order: a deeper address before its parent, so wouter cannot match the
 * module home first (`/booking/hq-cruise-space/live` before
 * `/booking/hq-cruise-space` before `/booking`).
 */
export const PHASE1_ROUTE_ORDER: readonly Phase1PageId[] = [...PHASE1_PAGE_IDS].sort(
  (a, b) => PHASE1_PAGES[b].path.length - PHASE1_PAGES[a].path.length,
);

export function phase1PageIdFor(location: string): Phase1PageId | null {
  return PHASE1_PAGE_IDS.find((id) => PHASE1_PAGES[id].path === location) ?? null;
}

// ---------------------------------------------------------------------------
// Modules — the Level-1 rows
// ---------------------------------------------------------------------------

export type Phase1ModuleId =
  | 'dashboard'
  | 'business'
  | 'running'
  | 'threads'
  | 'marketing'
  | 'crm'
  | 'booking'
  | 'finances'
  | 'reports'
  | 'calendar'
  | 'academy'
  | 'suppliers'
  | 'news'
  | 'everything'
  | 'todo'
  | 'bookmarks'
  | 'support'
  | 'whatsnew';

export type Phase1GroupId = 'loop' | 'workspace' | 'network' | 'me';

/** A row in a Level-2 column: a page, a group heading, or a Launch action. */
export type Phase1ColumnItem =
  | { kind: 'page'; page: Phase1PageId; label?: string }
  | { kind: 'heading'; label: string }
  | { kind: 'action'; page: Phase1PageId; label: string; external?: boolean };

export interface Phase1Module {
  id: Phase1ModuleId;
  group: Phase1GroupId;
  label: string;
  /** Lucide icon name — resolved in the rail, so this file stays free of React. */
  icon: string;
  /** Where the row lands. Absent for the Bookmarks row, which opens a panel. */
  path?: string;
  /** Extra path prefixes that also light the row. */
  matchPaths?: readonly string[];
  /** The first phase that shows the row. Phase 1 renders only rows with `from <= 1`. */
  from: 1 | 2 | 3 | 4;
  /** The Level-2 column. Absent = solo: the row is its own page (PH_SOLO). */
  column?: readonly Phase1ColumnItem[];
  /** Hover text — the module this row belongs to in the platform's nine-module map. */
  owner?: string;
}

export const PHASE1_GROUP_LABELS: Record<Phase1GroupId, string> = {
  loop: '',
  workspace: 'Workspace',
  network: 'Network',
  me: 'WorldVia Me',
};

const page = (id: Phase1PageId, label?: string): Phase1ColumnItem => ({ kind: 'page', page: id, label });

/**
 * The rows, in rail order (NAV in the prototype). `from` is PH_DROP read the
 * other way round: Business, Running, Reports, To-do and What's New are absent
 * until a later phase. Labels are the Phase-1 ones (PH_LABEL_MAP): the CRM row
 * reads "CRM (legacy)" and the Community row reads "News" with the file glyph.
 */
export const PHASE1_MODULES: readonly Phase1Module[] = [
  { id: 'dashboard', group: 'loop', label: 'Dashboard', icon: 'LayoutDashboard', path: '/', from: 1, owner: 'Today' },
  { id: 'business', group: 'loop', label: 'Business', icon: 'Eye', from: 4, owner: 'Business' },
  { id: 'running', group: 'loop', label: 'Running', icon: 'Zap', from: 4, owner: 'Running' },

  { id: 'threads', group: 'workspace', label: 'Threads', icon: 'MessageCircle', path: '/threads', matchPaths: ['/threads'], from: 1, owner: 'Threads' },
  {
    id: 'marketing',
    group: 'workspace',
    label: 'Marketing',
    icon: 'Megaphone',
    path: '/marketing',
    matchPaths: ['/marketing'],
    from: 1,
    owner: 'Marketing',
    // One flat list (Jason 2026-08-09). Content Calendar is Phase 2 (PH_KID_OFF).
    column: [
      page('marketing-web-profiles'),
      page('marketing-website'),
      page('marketing-email'),
      page('marketing-direct-mail'),
      page('marketing-social'),
      page('marketing-promo-products'),
      page('marketing-email-newsletters'),
      page('marketing-dynamic-promotions'),
      page('marketing-design-studio'),
      page('marketing-playbooks'),
    ],
  },
  { id: 'crm', group: 'workspace', label: 'CRM (legacy)', icon: 'IdCard', path: '/crm', matchPaths: ['/crm'], from: 1, owner: 'CRM' },
  {
    id: 'booking',
    group: 'workspace',
    label: 'Booking',
    icon: 'Plane',
    path: '/booking',
    matchPaths: ['/booking'],
    from: 1,
    owner: 'Booking',
    // PH_KIDS[1].booking + PH_ACTS[1].booking (Jason 2026-08-11): what the
    // network holds for you, then the reading, then the Launch actions.
    column: [
      page('booking-hq-cruise-space'),
      page('booking-rate-programs'),
      { kind: 'heading', label: 'About Our Tools' },
      page('booking-about-trio'),
      page('booking-about-travelport'),
      page('booking-about-vax'),
      page('booking-about-taap'),
      { kind: 'heading', label: 'Launch' },
      { kind: 'action', page: 'booking-launch-trio', label: 'Book in TRIO', external: true },
      { kind: 'action', page: 'booking-launch-travelport', label: 'Book in Travelport Cruise', external: true },
      { kind: 'action', page: 'booking-launch-vax', label: 'Book in VAX', external: true },
      { kind: 'action', page: 'booking-launch-taap', label: 'Book in Expedia TAAP', external: true },
      { kind: 'action', page: 'booking-agent-air', label: 'Book in Agent Air' },
    ],
  },
  { id: 'finances', group: 'workspace', label: 'Finances', icon: 'Receipt', from: 1, owner: 'Finances' },
  { id: 'reports', group: 'workspace', label: 'Reports', icon: 'ChartColumn', from: 3, owner: 'Reports' },

  // Network order: Calendar, Academy, Suppliers, News, Everything WorldVia (Jason 2026-09-11)
  { id: 'calendar', group: 'network', label: 'Calendar', icon: 'Calendar', path: '/calendar', matchPaths: ['/calendar'], from: 1, owner: 'Calendar' },
  { id: 'academy', group: 'network', label: 'Academy', icon: 'GraduationCap', path: '/academy', matchPaths: ['/academy'], from: 1, owner: 'Academy' },
  {
    id: 'suppliers',
    group: 'network',
    label: 'Suppliers',
    icon: 'Handshake',
    path: '/suppliers',
    matchPaths: ['/suppliers'],
    from: 1,
    owner: 'Suppliers',
    column: [page('suppliers-yours'), page('suppliers-promotions'), page('suppliers-fams')],
  },
  { id: 'news', group: 'network', label: 'News', icon: 'FileText', path: '/news', matchPaths: ['/news'], from: 1, owner: 'Community' },
  {
    id: 'everything',
    group: 'network',
    label: 'Everything WorldVia',
    icon: 'Compass',
    path: '/everything-worldvia',
    matchPaths: ['/everything-worldvia'],
    from: 1,
    owner: 'Everything WorldVia',
  },

  { id: 'todo', group: 'me', label: 'To-do', icon: 'SquareCheck', from: 3, owner: 'To-do' },
  { id: 'bookmarks', group: 'me', label: 'Bookmarks', icon: 'Bookmark', from: 1, owner: 'Bookmarks' },
  { id: 'support', group: 'me', label: 'Support', icon: 'Headphones', path: '/support', matchPaths: ['/support'], from: 1, owner: 'Support' },
  { id: 'whatsnew', group: 'me', label: "What's New", icon: 'Sparkles', from: 3, owner: "What's New" },
];

/** The column headings the Level-2 column prints for each module. */
export const PHASE1_COLUMN_HEADINGS: Partial<Record<Phase1ModuleId, string>> = {
  marketing: 'Marketing',
  booking: 'Booking',
  suppliers: 'Supplier Source',
};

/** The rows this phase renders. */
export const phase1VisibleModules = (phase = PHASE1_PHASE): Phase1Module[] =>
  PHASE1_MODULES.filter((m) => m.from <= phase);

export const phase1Module = (id: Phase1ModuleId): Phase1Module =>
  PHASE1_MODULES.find((m) => m.id === id)!;

/** The module a location sits inside, or null outside the Phase-1 screens. */
export function phase1ModuleFor(location: string): Phase1Module | null {
  const pageId = phase1PageIdFor(location);
  if (pageId) return phase1Module(PHASE1_PAGES[pageId].module as Phase1ModuleId);
  return (
    PHASE1_MODULES.find((m) =>
      (m.matchPaths ?? []).some((p) => location === p || location.startsWith(`${p}/`)),
    ) ?? null
  );
}

/**
 * The old CRM addresses. While Phase 1 is on, the CRM runs on WorldVia.pro, so
 * these land on the CRM (legacy) door for that surface rather than on a
 * workspace built on records the phase does not have (D-P1UX-CRED).
 */
export const PHASE1_CRM_REDIRECTS: Record<string, Phase1PageId> = {
  '/pipeline': 'crm-pipeline',
  '/sales-pipeline': 'crm-pipeline',
  '/clients': 'crm-clients',
  '/trips': 'crm-trips',
};

/**
 * Addresses the legacy app already used for a Phase-1 surface. With the flags
 * on they land on the new page; with them off the legacy route keeps them.
 */
export const PHASE1_LEGACY_REDIRECTS: Record<string, Phase1PageId> = {
  '/booking-tools': 'booking',
  '/training': 'academy',
};

export const isPhase1Path = (location: string): boolean => phase1ModuleFor(location) !== null;
